import { api } from '@shared/services/api/axios';
import {
  buildLoginAuthErrorUrl,
  logAuthApiFailure,
  saveAuthApiFailure,
} from '@shared/utils/authApiFailure';

const AUTH_REDIRECT_PATHS = new Set(['/login', '/callback']);

const extractErrorMessage = (error: {
  response?: { data?: { error?: string; message?: string } };
  message?: string;
}): string => {
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.message ||
    'Error de autenticación'
  );
};

export const configureAuthInterceptor = (getToken: () => Promise<string | null>) => {
  // Remover interceptores existentes para evitar duplicados
  api.interceptors.request.eject(0);
  api.interceptors.response.eject(0);

  const requestInterceptor = api.interceptors.request.use(
    async (config) => {
      try {
        const token = await getToken();

        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      } catch (error) {
        return Promise.reject(error);
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const responseInterceptor = api.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status as number | undefined;
      const errorMsg = extractErrorMessage(error);
      const isCallbackUrlError = errorMsg.includes('Callback URL mismatch');
      const isAuthError = status === 401 || status === 403;
      const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
      const isOnAuthPage = AUTH_REDIRECT_PATHS.has(pathname);

      // En /callback y /login no forzar redirect: Callback/LoginForm manejan el estado
      // y un hard redirect a /login sin flag reabre el bucle (Auth0 autenticado → /home).
      if (isOnAuthPage) {
        return Promise.reject(error);
      }

      if (isCallbackUrlError || isAuthError) {
        const failure = {
          status: status ?? 0,
          url: String(error?.config?.url || error?.config?.baseURL || 'unknown'),
          method: String(error?.config?.method || 'get').toUpperCase(),
          message: errorMsg,
        };
        logAuthApiFailure(failure);
        saveAuthApiFailure(failure);

        // Query param sobrevive window.location (a diferencia de react-router state)
        window.location.href = buildLoginAuthErrorUrl();
      }

      return Promise.reject(error);
    }
  );

  return () => {
    api.interceptors.request.eject(requestInterceptor);
    api.interceptors.response.eject(responseInterceptor);
  };
};
